import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { watchAuthState, signOut } from '../lib/firebase'
import { useUnreadCount } from '../routes/Notifications'
import { useTheme, toggleTheme } from '../lib/theme'
import { OPEN_MARKETPLACE } from '../lib/openMarketplace'
import { canUseAgenticSourcing } from '../lib/sourcing'
import { ASKA_PLUS_LIVE } from '../lib/askaServices'
import { meApi } from '../api/endpoints'
import { formatMemberSince } from '../lib/format'
import { useDevices } from '../lib/useDevices'
import { useIdentityProfile } from '../lib/identity'
import { usePendingInvite } from '../lib/usePendingInvite'

/**
 * Site-wide chrome. Mirrors the mobile drawer's wordmark anchor at the
 * top-left so users moving from app → web feel one continuous brand.
 * Web has more horizontal real estate so we surface a search pill,
 * chat entry, and cart/account inline rather than tucked in a drawer.
 *
 * Sell menu (for dual-role suppliers) and Sign in / Account avatar
 * are stubs for now; routes wire up as those surfaces ship.
 */
export function Header() {
  const navigate = useNavigate()
  const [signedIn, setSignedIn] = useState<boolean | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [role, setRole] = useState<'admin' | 'supplier' | 'buyer' | null>(null)
  const [memberSince, setMemberSince] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // Blur-on-scroll chrome: a near-transparent hairline at the top of the
  // page that condenses into a solid, bordered, softly-shadowed bar once
  // the user scrolls past ~8px. rAF-throttled so the listener never
  // thrashes layout; passive so it never blocks scrolling.
  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8)
        ticking = false
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState(async (user) => {
        setSignedIn(!!user)
        setEmail(user?.email ?? null)
        if (user) {
          try {
            const tok = await user.getIdTokenResult()
            const r = (tok.claims as { role?: string }).role
            setRole(r === 'admin' || r === 'supplier' ? r : 'buyer')
          } catch { setRole('buyer') }
          // "Member since" for the account menu — best-effort; a null
          // createdAt (older/migrated accounts) or a failed fetch simply
          // omits the line, never "Invalid Date".
          meApi
            .get()
            .then((r) => setMemberSince(formatMemberSince(r.user?.createdAt)))
            .catch(() => setMemberSince(null))
        } else {
          setRole(null)
          setMemberSince(null)
        }
      })
    } catch {
      // Firebase not configured yet — render the signed-out state.
      setSignedIn(false)
    }
    return () => unsub?.()
  }, [])

  // v2 chrome: flatter + sharper. At rest a quiet hairline; once scrolled,
  // the header's bottom edge becomes the THICK rule — a 2px navy-900 anchor
  // that separates the fixed chrome from the scrolling data (no gold tint,
  // no shadow — contrast comes from line weight).
  return (
    <header className="sticky top-0 z-40 w-full border-b border-line bg-cream/85 backdrop-blur-xl">
      {/* Floating liquid-glass pill — REAL refractive glass (liquid-glass-react,
          Tej 2026-08-03) over a high-contrast midnight tint. Displacement is
          Chromium-only; Safari/Firefox gracefully degrade to the tinted blur.
          Condenses slightly once scrolled (Apple tell). */}
      {/* liquid-glass-react proved layout-hostile for a full-width sticky bar
          (absolute internal wrapper) — the same high-contrast material is
          rendered in CSS (.header-glass); the lib stays for card-sized glass. */}
      <div
        className={`mx-auto flex max-w-[1760px] items-center justify-between px-4 sm:px-6 lg:px-8 transition-[height] duration-200 ease-premium ${
          scrolled ? 'h-[52px]' : 'h-14'
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Mobile hamburger — only visible < md */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-paper/60 transition-colors -ml-2"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-serif text-2xl leading-none tracking-tight text-ink">DiaEx</span>
            <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">by Aska</span>
          </Link>
        </div>

        <NavBar signedIn={signedIn} />

        <div className="flex items-center gap-2 md:gap-3">
          <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            onClick={() => window.dispatchEvent(new CustomEvent('aska:open-spotlight'))}
            aria-label="Search inventory"
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-paper/60 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </motion.button>

          <ThemeToggle />
          {signedIn ? (
            <div className="flex items-center gap-2">
              <NotificationsBell />
              <AccountMenu
                email={email}
                role={role}
                memberSince={memberSince}
                onSignOut={async () => { await signOut(); navigate('/') }}
              />
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="inline-flex items-center justify-center h-9 md:h-10 px-4 rounded-full bg-paper border border-line text-ink font-medium text-sm hover:border-navy-300 transition-colors duration-micro"
                data-testid="sign-in"
              >
                Sign in
              </button>
              {/* "Get started" = create an account. It used to duplicate the
                  Sign in button's /signin target, so the header's two CTAs did
                  the same thing — /register is the honest destination (it links
                  back to /signin for existing accounts). */}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="hidden sm:inline-flex items-center justify-center h-9 md:h-10 px-4 rounded-full bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-300 transition-colors duration-micro"
              >
                Get started
              </button>
            </>
          )}
        </div>
      </div>

      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        signedIn={!!signedIn}
        email={email}
        role={role}
      />
    </header>
  )
}

/**
 * Desktop primary nav. The active item carries a gold underline that
 * slides between items via a shared `layoutId` element (spec #10) —
 * inactive items are navy-500 → navy-700 on hover (120ms). Gold appears
 * ONLY under the active item, keeping it a true accent.
 */
// Primary nav = the working surfaces (Browse + AI). "Sell on DiaEx" is NOT a
// top-level tab (Tej 2026-08-05) — it lives on the home page, in the signup
// flow, and under the account (Profile) menu. "About" is marketing chrome:
// it shows for signed-out visitors only, and drops out once you're in the
// app — a signed-in trade user's header stays Browse / AI / account.
const NAV_ITEMS = [
  { to: '/search', label: 'Diamonds', signedOutOnly: false },
  // Ask Gemma is a first-class nav tab (Tej 2026-07-27) — the full-page AI
  // assistant. The floating pill stays as a quick in-context launcher, but the
  // tab is the primary, non-overlapping way in (it used to only be reachable
  // via the FAB, which covered options at the bottom of the page).
  { to: '/chat', label: 'Ask Gemma', signedOutOnly: false },
  { to: '/about', label: 'About', signedOutOnly: true },
] as const

function NavBar({ signedIn }: { signedIn: boolean | null }) {
  const location = useLocation()
  // Hide signed-out-only items the moment we know the user is signed in.
  // While auth is still resolving (null) we also hide them, so the header
  // never flashes "About" and then yanks it away.
  const items = NAV_ITEMS.filter((i) => !i.signedOutOnly || signedIn === false)
  return (
    <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
      {items.map((item) => {
        const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/')
        return (
          <Link
            key={item.to}
            to={item.to}
            className={`relative py-1 transition-colors duration-micro ${active ? 'text-ink' : 'text-navy-500 hover:text-ink'}`}
          >
            {item.label}
            {active && (
              <motion.span
                layoutId="nav-underline"
                className="absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full bg-gold-500"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * Slide-in drawer for sub-md viewports. Lists every primary route the
 * desktop nav exposes plus the authenticated buyer-side surfaces
 * (My Credit, KYC, Payment Methods, Orders) that desktop users reach
 * via separate pages. Without this, mobile-only users can only reach
 * /cart and /notifications from the header — every other surface is
 * unreachable.
 */
function MobileMenu({
  open, onClose, signedIn, email, role,
}: {
  open: boolean
  onClose: () => void
  signedIn: boolean
  email: string | null
  role: 'admin' | 'supplier' | 'buyer' | null
}) {
  const navigate = useNavigate()
  const location = useLocation()

  // Auto-close on route change so tapping a link feels right.
  useEffect(() => { onClose() }, [location.pathname])

  function go(path: string) {
    onClose()
    navigate(path)
  }

  const linkBase = 'block w-full text-left px-4 py-3 rounded-lg text-base text-ink hover:bg-page active:bg-page transition-colors'

  // Portal to <body> so the fixed overlay/panel escape the <header>'s
  // `backdrop-blur` filter — a `filter` establishes a containing block for
  // position:fixed descendants, which otherwise traps the drawer inside the
  // 64px header (it rendered collapsed/transparent before this fix).
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-[60] bg-navy/50 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="md:hidden fixed left-0 top-0 z-[70] h-dvh w-[78%] max-w-[320px] bg-paper shadow-card flex flex-col"
            role="dialog"
            aria-label="Main menu"
          >
            <div className="flex items-center justify-between h-16 px-4 border-b border-border/60">
              <span className="font-serif text-2xl text-ink">DiaEx</span>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-page"
                aria-label="Close menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-2">
              <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">Browse</p>
              <button className={linkBase} onClick={() => go('/search')}>Diamonds</button>
              <button className={linkBase} onClick={() => go('/chat')}>Ask Gemma</button>
              {/* "Sell on DiaEx" is no longer a primary nav item — it lives on
                  the home page, in signup, and (for buyers) under the account
                  section below. "About" is signed-out marketing chrome only. */}
              {!signedIn && (
                <button className={linkBase} onClick={() => go('/about')}>About</button>
              )}

              {signedIn && (
                <>
                  <div className="my-2 border-t border-border/60" />
                  <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">Your account</p>
                  {/* Credit + payment methods stay broker-only. No buyer Orders
                      link — v0 is a directory (no on-platform orders). */}
                  {canUseAgenticSourcing() && (
                    <button className={linkBase} onClick={() => go('/account/sourcing')}>My sourcing</button>
                  )}
                  <button className={linkBase} onClick={() => go('/shares')}>My shares</button>
                  <button className={linkBase} onClick={() => go('/favorites')}>Favorite suppliers</button>
                  {role === 'buyer' && (
                    <button className={linkBase} onClick={() => go('/sell')}>Sell on DiaEx</button>
                  )}
                  {!OPEN_MARKETPLACE && (
                    <button className={linkBase} onClick={() => go('/credit')}>Credit</button>
                  )}
                  <button className={linkBase} onClick={() => go('/kyc')}>KYC</button>
                  {!OPEN_MARKETPLACE && (
                    <button className={linkBase} onClick={() => go('/payment-methods')}>Payment methods</button>
                  )}
                  <button className={linkBase} onClick={() => go('/notifications')}>Notifications</button>

                  {(role === 'admin' || role === 'supplier') && (
                    <>
                      <div className="my-2 border-t border-border/60" />
                      <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">
                        {role === 'admin' ? 'Admin' : 'Supplier'}
                      </p>
                      {role === 'admin' && (
                        <>
                          <button className={linkBase} onClick={() => go('/admin')}>Admin · console</button>
                          <button className={linkBase} onClick={() => go('/admin?tab=analytics')}>Admin · analytics</button>
                          <button className={linkBase} onClick={() => go('/admin?tab=chat')}>Admin · chat history</button>
                          <button className={linkBase} onClick={() => go('/admin?tab=kb')}>Admin · knowledge base</button>
                        </>
                      )}
                      <button className={linkBase} onClick={() => go('/seller-inbox')}>Supplier inbox</button>
                      <button className={linkBase} onClick={() => go('/seller/mystock')}>Supplier · my stock</button>
                      <button className={linkBase} onClick={() => go('/sell/integrations')}>Supplier · integrations</button>
                    </>
                  )}
                </>
              )}
            </nav>

            <div className="border-t border-border/60 p-4">
              {signedIn ? (
                <>
                  {email && <p className="text-xs text-soft truncate mb-2">{email}</p>}
                  <button
                    type="button"
                    onClick={async () => { await signOut(); onClose(); navigate('/') }}
                    className="btn-secondary w-full h-11"
                    data-testid="mobile-menu-sign-out"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => go('/signin')}
                  className="btn-primary w-full h-11"
                  data-testid="mobile-menu-sign-in"
                >
                  Sign in
                </button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  )
}

/** Two-letter monogram from the account email — first letters of the two
 *  leading local-part segments (foo.bar → FB), else the first two chars. */
function initialsFor(email: string | null): string {
  if (!email) return '·'
  const local = email.split('@')[0] ?? email
  const parts = local.split(/[.\-_+]/).filter(Boolean)
  const chars = parts.length >= 2
    ? parts[0][0] + parts[1][0]
    : local.slice(0, 2)
  return chars.toUpperCase() || '·'
}

const ROLE_LABEL: Record<'admin' | 'supplier' | 'buyer', string> = {
  admin: 'Admin', supplier: 'Supplier', buyer: 'Buyer',
}

/**
 * Desktop account control. Replaces the raw email + inline Sign out button
 * with an avatar chip (navy circle, cream monogram) + chevron that opens a
 * right-aligned dropdown: the account email, a role badge, and role-aware
 * shortcuts. Closes on click-outside and Esc.
 *
 * The trigger keeps `data-testid="sign-out"` because the browser QA gate
 * (scripts/qa/browser-accounts.mjs) waits on that selector, visible, as its
 * signed-in indicator — it's the entry point to signing out. The actual
 * Sign out row carries `data-testid="account-menu-sign-out"`.
 */
export function AccountMenu({
  email, role, memberSince, onSignOut,
}: {
  email: string | null
  role: 'admin' | 'supplier' | 'buyer' | null
  memberSince: string | null
  onSignOut: () => void | Promise<void>
}) {
  const navigate = useNavigate()
  // ── ORG ENTRIES APPEAR ONLY WHEN ORGS ARE ACTUALLY ON ──────────────────
  // `GET /me` carries an `org` block ONLY when ORGS_LIVE is armed server-side
  // (functions/src/org/index.ts::orgBlockForUserDoc returns null otherwise), so
  // this is the flag, read from the server rather than mirrored into a client
  // constant. With the flags off the menu is byte-for-byte what it is today.
  const { org, orgRole } = useIdentityProfile()
  // ── THE INVITE'S WAY BACK ──────────────────────────────────────────────
  // `InviteWatcher` delivers an emailed invite to `/invite` exactly ONCE (it
  // used to pin the user there on every navigation — a trap with no exit). So
  // the invite has to be reachable from somewhere ordinary afterwards, or
  // pressing "Not now" would cost the invitee their invite until they went
  // back to their inbox. Gated on a stashed token, so it does not exist unless
  // an invite link was actually opened — i.e. never while ORGS_LIVE is off.
  const pendingInvite = usePendingInvite()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  // `ready` only. While DEVICE_LIMIT_LIVE is off this is `unavailable` and the
  // item is not rendered at all — a feature that is off must be invisible, not
  // a menu row that lands on an explanation nobody asked for.
  const devices = useDevices()

  // Click-outside + Esc dismissal, only wired while the menu is open.
  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function go(path: string) {
    setOpen(false)
    navigate(path)
  }

  const itemBase = 'flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm text-ink text-left hover:bg-page transition-colors'

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 h-10 rounded-full border border-border bg-paper pl-1 pr-2.5 hover:border-primary/30 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="sign-out"
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-navy text-[#f4ead1] text-xs font-bold tracking-tight">
          {initialsFor(email)}
        </span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`text-soft transition-transform duration-200 ease-premium ${open ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full mt-2 w-[240px] max-w-[calc(100vw-1.5rem)] origin-top-right rounded-card border border-border bg-paper p-1.5 shadow-lift z-50"
            role="menu"
            aria-label="Account"
            data-testid="account-menu"
          >
            <div className="px-3 pt-2 pb-2.5 border-b border-border/60">
              <p className="text-sm font-semibold text-ink truncate">{email ?? 'Account'}</p>
              {role && (
                <span className="mt-1.5 inline-flex items-center rounded-full bg-navy px-2 py-0.5 text-[11px] font-semibold text-[#f4ead1]">
                  {ROLE_LABEL[role]}
                </span>
              )}
              {/* "Member since" — omitted when no valid date (older/migrated
                  accounts), never rendered as a placeholder. */}
              {memberSince && (
                <p className="mt-1.5 text-[11px] text-soft" data-testid="account-menu-member-since">
                  Member since {memberSince}
                </p>
              )}
            </div>

            <div className="pt-1.5">
              {/* Profile & settings — EVERY role. This is where a supplier fixes a
                  wrong phone / adds a country's WhatsApp line, and where a buyer
                  edits their company + address. */}
              <button
                role="menuitem"
                className={itemBase}
                onClick={() => go('/account/profile')}
                data-testid="account-menu-profile"
              >
                Profile &amp; settings
              </button>
              <div className="my-1 border-t border-border/60" />
              {canUseAgenticSourcing() && (
                <button role="menuitem" className={itemBase} onClick={() => go('/account/sourcing')} data-testid="account-menu-sourcing">My sourcing</button>
              )}
              {role === 'buyer' && (
                <>
                  <button role="menuitem" className={itemBase} onClick={() => go('/shares')}>My shares</button>
                  <button role="menuitem" className={itemBase} onClick={() => go('/favorites')}>Favorite suppliers</button>
                  <button role="menuitem" className={itemBase} onClick={() => go('/kyc')}>KYC &amp; verification</button>
                  {/* Become-a-supplier lives here (Tej 2026-08-05) instead of a
                      top-level nav tab — plus the home page + signup CTAs. */}
                  <button role="menuitem" className={itemBase} onClick={() => go('/sell')}>Sell on DiaEx</button>
                </>
              )}
              {role === 'supplier' && (
                <>
                  <button role="menuitem" className={itemBase} onClick={() => go('/seller/mystock')}>My stock</button>
                  {/* "Sell" (the become-a-supplier pitch) is pointless for a
                      supplier — their working surface is integrations. */}
                  <button role="menuitem" className={itemBase} onClick={() => go('/sell/integrations')}>Stock feeds</button>
                </>
              )}
              {role === 'admin' && (
                <button role="menuitem" className={itemBase} onClick={() => go('/admin')}>Admin console</button>
              )}
              {pendingInvite && (
                <>
                  <div className="my-1 border-t border-border/60" />
                  <button
                    role="menuitem"
                    className={itemBase}
                    onClick={() => go('/invite')}
                    data-testid="account-menu-invite"
                  >
                    Your team invite
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-gold-500" aria-hidden />
                  </button>
                </>
              )}
              {/* Admins are platform staff, not a buyer/supplier org — the
                  solo org /me auto-mints for them is an artifact, so hide the
                  Company + Team self-service (Tej 2026-09-21). They manage
                  everything from the /admin console. */}
              {org && role !== 'admin' && (
                <>
                  <div className="my-1 border-t border-border/60" />
                  <button
                    role="menuitem"
                    className={itemBase}
                    onClick={() => go('/account/company')}
                    data-testid="account-menu-company"
                  >
                    {org.legalName || 'Your company'}
                  </button>
                  <button
                    role="menuitem"
                    className={itemBase}
                    onClick={() => go('/account/team')}
                    data-testid="account-menu-team"
                  >
                    Team
                    {orgRole === 'owner' || orgRole === 'admin' ? (
                      <span className="tabular ml-auto text-[11px] text-soft">
                        {org.seats.used}/{org.seats.limit}
                      </span>
                    ) : null}
                  </button>
                </>
              )}
              {/* Aska+ membership — display-gated on ASKA_PLUS_LIVE (the join
                  CTA inside the screen is separately gated on the billing flag +
                  native shell). Every buyer sees the entry; the screen handles
                  member vs non-member. */}
              {ASKA_PLUS_LIVE && (
                <button
                  role="menuitem"
                  className={itemBase}
                  onClick={() => go('/account/membership')}
                  data-testid="account-menu-membership"
                >
                  Aska+
                </button>
              )}
              {/* Every role, not buyer-only: a supplier signs in from three
                  machines exactly as often as a buyer does. */}
              {devices.status === 'ready' && (
                <button
                  role="menuitem"
                  className={itemBase}
                  onClick={() => go('/account/devices')}
                  data-testid="account-menu-devices"
                >
                  Devices &amp; sessions
                  <span className="ml-auto text-[11px] text-soft tabular">
                    {devices.exempt ? '' : `${devices.used}/${devices.limit}`}
                  </span>
                </button>
              )}

              <div className="my-1 border-t border-border/60" />
              <button
                role="menuitem"
                className={`${itemBase} text-danger hover:bg-danger/5`}
                onClick={() => { setOpen(false); void onSignOut() }}
                data-testid="account-menu-sign-out"
              >
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Light/dark switch. The whole palette is CSS vars keyed off
 * `[data-theme]` on <html>, so this just flips one attribute (lib/theme.ts)
 * and the entire tree re-themes. Sun in dark mode (tap → light), moon in
 * light mode (tap → dark).
 */
function ThemeToggle() {
  const theme = useTheme()
  const dark = theme === 'dark'
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.92 }}
      onClick={() => toggleTheme()}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-page transition-colors"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
      data-testid="theme-toggle"
    >
      {dark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2v2.5M12 19.5V22M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M2 12h2.5M19.5 12H22M4.6 19.4l1.8-1.8M17.6 6.4l1.8-1.8" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 14.5A8 8 0 1 1 9.5 4a6.3 6.3 0 0 0 10.5 10.5Z" />
        </svg>
      )}
    </motion.button>
  )
}

function NotificationsBell() {
  const navigate = useNavigate()
  const count = useUnreadCount()
  return (
    <motion.button
      type="button"
      whileTap={{ scale: 0.96 }}
      onClick={() => navigate('/notifications')}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-page transition-colors"
      aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center pointer-events-none">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </motion.button>
  )
}
